import { useEffect, useMemo, useRef, useState } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import {
  formatLayoutDefinition,
  generateLayoutAiInstructions,
  parseLayoutDefinition,
  saveFileLayoutVersion,
  saveInputFromDefinition,
  validateLayoutDefinition,
  type DeclarativeFileLayout,
  type FileLayoutDirection,
  type FileLayoutFormat,
  type SavedFileLayoutReference,
} from '@/modules/file-reader'

export interface FileLayoutExampleOption {
  id: string
  label: string
  partyId: string
  documentId: string
}

interface FileLayoutJsonModalProps {
  isOpen: boolean
  unitId: string
  layoutId: string | null
  initialDefinition: DeclarativeFileLayout
  initialTab?: EditorTab
  examples: FileLayoutExampleOption[]
  onClose: () => void
  onSaved: (saved: SavedFileLayoutReference) => void
  onTest: (example: FileLayoutExampleOption, layoutId: string | null) => void
}

type EditorTab = 'json' | 'ai' | 'test'

const INPUT_CLASS = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:[color-scheme:dark]'

function downloadText(fileName: string, content: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }))
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

export function FileLayoutJsonModal({
  isOpen,
  unitId,
  layoutId,
  initialDefinition,
  initialTab = 'json',
  examples,
  onClose,
  onSaved,
  onTest,
}: FileLayoutJsonModalProps) {
  const { t, locale } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [activeTab, setActiveTab] = useState<EditorTab>(initialTab)
  const [jsonText, setJsonText] = useState('')
  const [selectedExampleId, setSelectedExampleId] = useState('')
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'warning'; lines: string[] } | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setActiveTab(initialTab)
    setJsonText(formatLayoutDefinition(initialDefinition))
    setSelectedExampleId(examples[0]?.id || '')
    setFeedback(null)
    setCopied(false)
  }, [examples, initialDefinition, initialTab, isOpen])

  const parsedDefinition = useMemo(() => {
    try { return parseLayoutDefinition(jsonText) } catch { return null }
  }, [jsonText])
  const promptDefinition = parsedDefinition || initialDefinition
  const aiInstructions = useMemo(
    () => generateLayoutAiInstructions(promptDefinition, locale),
    [locale, promptDefinition],
  )
  const selectedExample = examples.find((example) => example.id === selectedExampleId) || null

  const updateDefinitionOption = (field: 'direction' | 'format', value: FileLayoutDirection | FileLayoutFormat) => {
    if (!parsedDefinition) return
    const next = { ...parsedDefinition, [field]: value }
    setJsonText(formatLayoutDefinition(next))
    setFeedback(null)
    setCopied(false)
  }

  const validate = () => {
    try {
      const definition = parseLayoutDefinition(jsonText)
      const result = validateLayoutDefinition(definition)
      if (!result.valid) {
        setFeedback({ type: 'error', lines: result.errors.map((error) => t('accounting.layouts.editor.validationField', { field: error })) })
        return null
      }
      const lines = result.warnings.map((warning) => t('accounting.layouts.editor.validationWarning', { field: warning }))
      setFeedback({ type: lines.length > 0 ? 'warning' : 'success', lines: lines.length > 0 ? lines : [t('accounting.layouts.editor.valid')] })
      return definition
    } catch {
      setFeedback({ type: 'error', lines: [t('accounting.layouts.editor.invalidJson')] })
      return null
    }
  }

  const save = async () => {
    const definition = validate()
    if (!definition || saving) return
    setSaving(true)
    try {
      const saved = await saveFileLayoutVersion(saveInputFromDefinition(definition, unitId, layoutId))
      onSaved(saved)
      onClose()
    } catch (error) {
      console.error('[FileLayoutJsonModal] Save error:', error)
      setFeedback({ type: 'error', lines: [t('accounting.layouts.editor.saveError')] })
    } finally {
      setSaving(false)
    }
  }

  const formatJson = () => {
    try {
      setJsonText(formatLayoutDefinition(parseLayoutDefinition(jsonText)))
      setFeedback(null)
    } catch {
      setFeedback({ type: 'error', lines: [t('accounting.layouts.editor.invalidJson')] })
    }
  }

  const importJson = async (file: File | null) => {
    if (!file) return
    try {
      const text = await file.text()
      const definition = parseLayoutDefinition(text)
      setJsonText(formatLayoutDefinition(definition))
      setFeedback(null)
      setActiveTab('json')
    } catch {
      setFeedback({ type: 'error', lines: [t('accounting.layouts.editor.invalidJson')] })
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const copyInstructions = async () => {
    try {
      await navigator.clipboard.writeText(aiInstructions)
      setCopied(true)
    } catch {
      setFeedback({ type: 'error', lines: [t('accounting.layouts.editor.copyError')] })
    }
  }

  const safeFileName = (parsedDefinition?.key || initialDefinition.key || 'leiaute').replace(/[^a-zA-Z0-9_-]+/g, '_')

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t(layoutId ? 'accounting.layouts.editor.editTitle' : 'accounting.layouts.editor.newTitle')}
      size="lg"
      onSubmit={() => void save()}
      submitLabel={t('accounting.layouts.editor.save')}
      submitting={saving}
      closeLabel={t('actions.close')}
    >
      <div className="space-y-4">
        <nav className="flex gap-1 overflow-x-auto border-b border-slate-200 dark:border-slate-700" aria-label={t('accounting.layouts.editor.tabs.label')}>
          {(['json', 'ai', 'test'] as EditorTab[]).map((tab) => <button key={tab} type="button" onClick={() => setActiveTab(tab)} className={`shrink-0 border-b-2 px-4 py-3 text-sm font-semibold ${activeTab === tab ? 'border-emerald-500 text-emerald-700 dark:text-emerald-300' : 'border-transparent text-slate-500 dark:text-slate-400'}`}>{t(`accounting.layouts.editor.tabs.${tab}`)}</button>)}
        </nav>

        {feedback && <div className={`rounded-lg border p-3 text-sm ${feedback.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300' : feedback.type === 'warning' ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300' : 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300'}`}>
          {feedback.lines.map((line) => <p key={line}>{line}</p>)}
        </div>}

        {activeTab === 'json' && <section className="space-y-3">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">{t('accounting.layouts.editor.jsonTitle')}</h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t('accounting.layouts.editor.jsonHint')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => fileInputRef.current?.click()} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700">{t('accounting.layouts.editor.importJson')}</button>
            <button type="button" onClick={formatJson} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700">{t('accounting.layouts.editor.formatJson')}</button>
            <button type="button" onClick={validate} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700">{t('accounting.layouts.editor.validate')}</button>
            <button type="button" onClick={() => downloadText(`${safeFileName}.json`, jsonText, 'application/json')} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700">{t('accounting.layouts.editor.downloadJson')}</button>
          </div>
          <input ref={fileInputRef} type="file" accept="application/json,.json" className="hidden" onChange={(event) => void importJson(event.target.files?.[0] || null)} />
          <textarea value={jsonText} onChange={(event) => { setJsonText(event.target.value); setFeedback(null); setCopied(false) }} spellCheck={false} className="h-[52vh] w-full resize-none rounded-xl border border-slate-300 bg-slate-950 p-4 font-mono text-xs leading-5 text-slate-100 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-600" />
        </section>}

        {activeTab === 'ai' && <section className="space-y-4">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">{t('accounting.layouts.editor.aiTitle')}</h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t('accounting.layouts.editor.aiHint')}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('accounting.layouts.editor.direction')}<select value={promptDefinition.direction} disabled={!parsedDefinition} onChange={(event) => updateDefinitionOption('direction', event.target.value as FileLayoutDirection)} className={`${INPUT_CLASS} mt-1`}><option value="import">{t('accounting.layouts.editor.directions.import')}</option><option value="export">{t('accounting.layouts.editor.directions.export')}</option><option value="bidirectional">{t('accounting.layouts.editor.directions.bidirectional')}</option></select></label>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">{t('accounting.layouts.editor.format')}<select value={promptDefinition.format} disabled={!parsedDefinition} onChange={(event) => updateDefinitionOption('format', event.target.value as FileLayoutFormat)} className={`${INPUT_CLASS} mt-1`}><option value="xml">XML</option><option value="delimited_text">{t('accounting.layouts.editor.formats.delimited')}</option><option value="fixed_width_text">{t('accounting.layouts.editor.formats.fixed')}</option></select></label>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => void copyInstructions()} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">{copied ? t('accounting.layouts.editor.copied') : t('accounting.layouts.editor.copyInstructions')}</button>
            <button type="button" onClick={() => downloadText(`${safeFileName}-instrucoes-ia.txt`, aiInstructions, 'text/plain;charset=utf-8')} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700">{t('accounting.layouts.editor.downloadInstructions')}</button>
          </div>
          <textarea readOnly value={aiInstructions} className="h-[46vh] w-full resize-none rounded-xl border border-slate-300 bg-slate-50 p-4 font-mono text-xs leading-5 text-slate-800 outline-none dark:border-slate-600 dark:bg-slate-950 dark:text-slate-200" />
        </section>}

        {activeTab === 'test' && <section className="space-y-4">
          <div><h3 className="font-semibold text-slate-900 dark:text-white">{t('accounting.layouts.editor.testTitle')}</h3><p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t('accounting.layouts.editor.testHint')}</p></div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('accounting.layouts.editor.exampleDocument')}<select value={selectedExampleId} onChange={(event) => setSelectedExampleId(event.target.value)} className={`${INPUT_CLASS} mt-1`}><option value="">{t('accounting.layouts.noCompatibleDocuments')}</option>{examples.map((example) => <option key={example.id} value={example.id}>{example.label}</option>)}</select></label>
          <button type="button" disabled={!selectedExample || !layoutId} onClick={() => selectedExample && layoutId && onTest(selectedExample, layoutId)} className="rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50">{t('accounting.layouts.editor.openTest')}</button>
          {!layoutId && <p className="text-xs text-amber-700 dark:text-amber-300">{t('accounting.layouts.editor.saveBeforeTest')}</p>}
        </section>}
      </div>
    </Modal>
  )
}
